import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
  Collapse,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@material-ui/core';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Error,
  PlayArrow,
  GetApp,
  Assessment,
  Schedule,
  Person,
  LocationOn,
  Refresh
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  auditCard: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  statusChip: {
    fontWeight: 'bold',
  },
  inprogress: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  completed: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  cancelled: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[900],
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  discrepancyPositive: {
    color: theme.palette.success.main,
  },
  discrepancyNegative: {
    color: theme.palette.error.main,
  },
  detailSection: {
    marginTop: theme.spacing(3),
  },
  shelfProgress: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  itemRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  discrepancyCell: {
    fontWeight: 'bold',
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  valueCell: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
}));

const AuditReviewDashboard = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [conductorFilter, setConductorFilter] = useState('all');

  useEffect(() => {
    fetchAllAudits();
  }, []);

  const fetchAllAudits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${props.url}/api/shop/fetchAudits`);
      // Change this line - the response is now the array directly
      setAudits(response.data || []);
    } catch (error) {
      console.error('Error fetching audits:', error);
      props.notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load audits'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditDetails = async (auditId) => {
    console.log('fetchAuditDetails...', auditId)
    let audit = audits.find(a => (a?._id?.toString() === auditId?.toString()))
    if (audit?._id) {
      setSelectedAudit(audit);
      setExpandedAudit(auditId);
    }
  };

  const handleAuditClick = (audit) => {
    if (expandedAudit === audit._id) {
      setExpandedAudit(null);
      setSelectedAudit(null);
    } else {
      fetchAuditDetails(audit._id);
    }
  };

  const handleExportPDF = async (audit) => {
    // For now, export as CSV
    try {
      const response = await axios.get(
        `${props.url}/api/shop/audits/${audit._id}/export`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-${audit._id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      props.notification({
        type: 'success',
        title: 'Success',
        message: 'Audit exported successfully'
      });
    } catch (error) {
      console.error('Error exporting audit:', error);
      props.notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to export audit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle style={{ color: '#4caf50' }} />;
      case 'in-progress':
        return <PlayArrow style={{ color: '#2196f3' }} />;
      case 'cancelled':
        return <Error style={{ color: '#757575' }} />;
      default:
        return null;
    }
  };

  const calculateDiscrepancyTotal = (items) => {
    if (!items) return 0;
    return items.reduce((total, item) => {
      if (item.inventoryItem && item.status === 'matched') {
        return total + (item.countedQuantity - (item.inventoryItem.quantity || 0));
      }
      return total;
    }, 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDuration = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getFilteredAudits = () => {
    return audits.filter(audit => {
      if (statusFilter !== 'all' && audit.status !== statusFilter) return false;
      
      if (dateFilter !== 'all') {
        const auditDate = new Date(audit.startDate);
        const now = new Date();
        switch (dateFilter) {
          case 'today':
            if (auditDate.toDateString() !== now.toDateString()) return false;
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (auditDate < weekAgo) return false;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            if (auditDate < monthAgo) return false;
            break;
        }
      }
      
      if (conductorFilter !== 'all' && audit.conductorName !== conductorFilter) return false;
      
      return true;
    });
  };

  const renderAuditSummaryCard = (audit) => {
    const progress = audit.isShelfBased && audit.shelves
      ? (audit.shelves.filter(s => s.status === 'completed').length / audit.shelves.length) * 100
      : ((audit.summary?.matchedItems || 0) / (audit.summary?.totalItems || 1)) * 100;

    return (
      <Card 
        key={audit._id} 
        className={classes.auditCard}
        onClick={() => handleAuditClick(audit)}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" mb={1}>
                {getStatusIcon(audit.status)}
                <Typography variant="h6" style={{ marginLeft: 8 }}>
                  {audit.title}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                <Person fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {audit.conductorName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <Schedule fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {new Date(audit.startDate).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Items</Typography>
                  <Typography variant="h6">{audit.summary?.totalItems || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Discrepancies</Typography>
                  <Typography variant="h6" className={audit.summary?.discrepancies > 0 ? classes.discrepancyNegative : ''}>
                    {audit.summary?.discrepancies || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Progress</Typography>
                  <Box mt={1}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      className={classes.progressBar}
                      color={audit.status === 'completed' ? 'primary' : 'secondary'}
                    />
                    <Typography variant="caption">{Math.round(progress)}%</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Chip
              label={audit.status.toUpperCase()}
              size="small"
              className={`${classes.statusChip} ${classes[audit.status.replace('-', '')]}`}
            />
            <IconButton size="small">
              {expandedAudit === audit._id ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderAuditDetails = () => {
    if (!selectedAudit) return null;

    const discrepancyTotal = calculateDiscrepancyTotal(selectedAudit.items);

    return (
      <Collapse in={expandedAudit === selectedAudit._id}>
        <Paper className={classes.detailSection} elevation={3}>
          <Box p={3}>
            <Grid container spacing={3}>
              {/* Summary Statistics */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <Assessment style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Audit Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statValue}>
                        {selectedAudit.statistics?.totalItems || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Items Scanned
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statValue}>
                        {selectedAudit.statistics?.matchedItems || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Matched Items
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.statCard}>
                      <Typography 
                        className={`${classes.statValue} ${selectedAudit.statistics?.discrepancies > 0 ? classes.discrepancyNegative : classes.discrepancyPositive}`}
                      >
                        {selectedAudit.statistics?.discrepancies || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Discrepancies Found
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card className={classes.statCard}>
                      <Typography 
                        className={`${classes.statValue} ${discrepancyTotal !== 0 ? (discrepancyTotal > 0 ? classes.discrepancyPositive : classes.discrepancyNegative) : ''}`}
                      >
                        {discrepancyTotal > 0 ? '+' : ''}{discrepancyTotal}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Net Quantity Change
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Additional Stats Row */}
                {selectedAudit.statistics?.valueOfDiscrepancies !== undefined && (
                  <Grid container spacing={2} style={{ marginTop: 16 }}>
                    <Grid item xs={6} sm={3}>
                      <Card className={classes.statCard}>
                        <Typography className={`${classes.statValue} ${classes.valueCell}`}>
                          {formatCurrency(Math.abs(selectedAudit.statistics.valueOfDiscrepancies))}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Value Impact
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card className={classes.statCard}>
                        <Typography className={classes.statValue}>
                          {selectedAudit.duration ? formatDuration(selectedAudit.duration) : 'In Progress'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Duration
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {/* Shelf Progress (if shelf-based) */}
              {selectedAudit.isShelfBased && selectedAudit.shelves && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    <LocationOn style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Shelf Progress
                  </Typography>
                  <Paper className={classes.shelfProgress}>
                    <Grid container spacing={2}>
                      {selectedAudit.shelves.map((shelf) => (
                        <Grid item xs={12} sm={6} md={4} key={shelf.shelfId}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body1">{shelf.shelfName}</Typography>
                            <Chip
                              size="small"
                              label={shelf.status}
                              color={shelf.status === 'completed' ? 'primary' : 'default'}
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {selectedAudit.itemsByShelf?.[shelf.shelfId]?.length || 0} items
                          </Typography>
                          {shelf.auditedBy && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              By: {shelf.auditedByName || 'Unknown'}
                            </Typography>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Tabs for different views */}
              <Grid item xs={12}>
                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => setTabValue(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="All Items" />
                  <Tab label="Discrepancies Only" />
                  <Tab label="By Shelf" disabled={!selectedAudit.isShelfBased} />
                  <Tab label="Top Issues" />
                </Tabs>

                {/* All Items Tab */}
                {tabValue === 0 && (
                  <TableContainer component={Paper} style={{ marginTop: 16 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell>Barcode/Part #</TableCell>
                          <TableCell align="right">System Qty</TableCell>
                          <TableCell align="right">Counted Qty</TableCell>
                          <TableCell align="right">Difference</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAudit.items?.map((item, index) => {
                          const systemQty = item.inventoryItem?.quantity || 0;
                          const difference = item.countedQuantity - systemQty;
                          
                          return (
                            <TableRow key={index} className={classes.itemRow}>
                              <TableCell>
                                {item.inventoryItem?.title || item.tempInfo?.title || 'Unknown Item'}
                              </TableCell>
                              <TableCell>
                                {item.barcode || item.partNumber || 'N/A'}
                              </TableCell>
                              <TableCell align="right">{systemQty}</TableCell>
                              <TableCell align="right">{item.countedQuantity}</TableCell>
                              <TableCell 
                                align="right" 
                                className={`${classes.discrepancyCell} ${difference !== 0 ? (difference > 0 ? classes.discrepancyPositive : classes.discrepancyNegative) : ''}`}
                              >
                                {difference > 0 ? '+' : ''}{difference}
                              </TableCell>
                              <TableCell>{item.locationFound || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={item.status}
                                  color={item.status === 'matched' ? 'primary' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Discrepancies Only Tab */}
                {tabValue === 1 && (
                  <TableContainer component={Paper} style={{ marginTop: 16 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell>Barcode/Part #</TableCell>
                          <TableCell align="right">System Qty</TableCell>
                          <TableCell align="right">Counted Qty</TableCell>
                          <TableCell align="right">Difference</TableCell>
                          <TableCell align="right">Value Impact</TableCell>
                          <TableCell>Location</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAudit.discrepancyDetails
                          ?.map((item, index) => (
                            <TableRow key={index} className={classes.itemRow}>
                              <TableCell>{item.item}</TableCell>
                              <TableCell>{item.barcode || item.partNumber || 'N/A'}</TableCell>
                              <TableCell align="right">{item.systemQuantity}</TableCell>
                              <TableCell align="right">{item.countedQuantity}</TableCell>
                              <TableCell 
                                align="right" 
                                className={`${classes.discrepancyCell} ${item.difference > 0 ? classes.discrepancyPositive : classes.discrepancyNegative}`}
                              >
                                {item.difference > 0 ? '+' : ''}{item.difference}
                              </TableCell>
                              <TableCell align="right" className={classes.valueCell}>
                                {formatCurrency(Math.abs(item.totalCostImpact))}
                              </TableCell>
                              <TableCell>{item.location || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* By Shelf Tab */}
                {tabValue === 2 && selectedAudit.isShelfBased && (
                  <Box mt={2}>
                    {selectedAudit.shelves?.map((shelf) => (
                      <Paper key={shelf.shelfId} style={{ marginBottom: 16, padding: 16 }}>
                        <Typography variant="h6" gutterBottom>
                          {shelf.shelfName} - {shelf.status}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Counted</TableCell>
                                <TableCell align="right">System</TableCell>
                                <TableCell align="right">Diff</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedAudit.itemsByShelf?.[shelf.shelfId]?.map((item, idx) => {
                                const systemQty = item.inventoryItem?.quantity || 0;
                                const diff = item.countedQuantity - systemQty;
                                return (
                                  <TableRow key={idx}>
                                    <TableCell>{item.inventoryItem?.title || 'Unknown'}</TableCell>
                                    <TableCell align="right">{item.countedQuantity}</TableCell>
                                    <TableCell align="right">{systemQty}</TableCell>
                                    <TableCell align="right" className={diff !== 0 ? (diff > 0 ? classes.discrepancyPositive : classes.discrepancyNegative) : ''}>
                                      {diff > 0 ? '+' : ''}{diff}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    ))}
                  </Box>
                )}

                {/* Top Issues Tab */}
                {tabValue === 3 && (
                  <Box mt={2}>
                    <Paper style={{ padding: 16 }}>
                      <Typography variant="h6" gutterBottom>
                        Top Discrepancies by Value Impact
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Rank</TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell align="right">Qty Diff</TableCell>
                              <TableCell align="right">Unit Cost</TableCell>
                              <TableCell align="right">Total Impact</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedAudit.discrepancyDetails
                              ?.slice(0, 10)
                              .map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>{item.item}</TableCell>
                                  <TableCell align="right" className={item.difference > 0 ? classes.discrepancyPositive : classes.discrepancyNegative}>
                                    {item.difference > 0 ? '+' : ''}{item.difference}
                                  </TableCell>
                                  <TableCell align="right">{formatCurrency(item.costPerUnit)}</TableCell>
                                  <TableCell align="right" className={classes.valueCell}>
                                    {formatCurrency(Math.abs(item.totalCostImpact))}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                )}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={<GetApp />}
                    onClick={() => handleExportPDF(selectedAudit)}
                  >
                    Export CSV
                  </Button>
               
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Collapse>
    );
  };

  const uniqueConductors = [...new Set(audits.map(a => a.conductorName).filter(Boolean))];

  return (
    <Box className={classes.root}>
      <Paper className={classes.header} elevation={0}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">Inventory Audit Review</Typography>
            <Typography variant="body2" color="textSecondary">
              Review and analyze completed and in-progress audits
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Refresh />}
              onClick={fetchAllAudits}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper className={classes.filterSection}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date Range"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Conductor</InputLabel>
              <Select
                value={conductorFilter}
                onChange={(e) => setConductorFilter(e.target.value)}
                label="Conductor"
              >
                <MenuItem value="all">All Conductors</MenuItem>
                {uniqueConductors.map(conductor => (
                  <MenuItem key={conductor} value={conductor}>{conductor}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Audit List */}
      {!loading && getFilteredAudits().length === 0 && (
        <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No audits found matching your filters
          </Typography>
        </Paper>
      )}

      {!loading && getFilteredAudits().map(audit => (
        <Box key={audit._id}>
          {renderAuditSummaryCard(audit)}
          {expandedAudit === audit._id && renderAuditDetails()}
        </Box>
      ))}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  notification: state.notification,
});

export default connect(mapStateToProps)(AuditReviewDashboard);
